import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
// import { useHistory } from 'react-router';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import FingerprintIcon from '../../components/Icons/FingerprintIcon';
import AccountingCoins from '../../components/Icons/AccountingCoins';
import BaseLayout from '../../components/BaseLayout';
import Header from '../../components/Header';
import SelectAccountDescription from '../../components/SelectAccountDescription';
import ButtonPrimary from '../../components/Buttons/Button';
import DataTransferIcon from '../../components/Icons/DataTransferIcon';
import useApplicationFundingData from '../../hooks/useApplicationFundingData';
import useBankApplicationFundingData from '../../hooks/useBankApplicationFundingData';
import useFundingTypeHook from '../../util/helpers/useFundingTypeHook';
import { parseEnumType } from '../../util/helpers/enumMappers';
import {
  APPLICATION_FUNDING_TYPE,
  IRA_APPLICATION_FUNDING_TYPES,
} from '../../util/constants';
import useIraApplicationFundingData from '../../hooks/useApplicationIraFundingData';
import { separateByUppercase } from '../../util/helpers/stringHelpers';
import { useMetalStreamData } from '../../hooks/useMetalStreamData';
import {
  selectPortalUrl,
  selectSettings,
} from '../../store/selectors/settingsSelectors';
import { retrieveFromSessionStorage } from '../../util/helpers/sessionStorageHelper';
import {
  ACCOUNT_TYPE_GTM,
  IS_UPGRADE_FLOW,
} from '../../constants/sessionStorage';
import useGtmStepHook from '../../util/hooks/useGtmStepHook';
import { ACCOUNT_STEP_COMPLETE, ACCOUNT_STEP_VIEW } from '../../util/gtmEvents';
import { gtmStepHelper } from '../../util/helpers/gtmHelper';
import { useApplicantData } from '../../hooks/useApplicantData';

const CTAContainer = styled.section`
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media only screen and (max-width: 500px) {
    flex-direction: column;
  }
`;

const KycSuccess = () => {
  const { t } = useTranslation();
  const ApplicantData = useApplicantData();
  const { ApplicationFunding } = useApplicationFundingData();
  const { BankApplicationFunding } = useBankApplicationFundingData();
  const { IraApplicationFundingResponse } = useIraApplicationFundingData();
  const { MetalStreamData } = useMetalStreamData();
  const [fundingMethod, setFundingMethod] = useState('');
  const [isMetalStreamPlaid, setIsMetalStreamPlaid] = useState(false);
  const portalUrlApi = useSelector(selectPortalUrl);
  const { BrokerName } = useSelector(selectSettings);
  const { gtmStepEvent } = useGtmStepHook();
  const isUpgradeFlowFromDashboard = retrieveFromSessionStorage(
    IS_UPGRADE_FLOW,
  );

  const { isPlaid } = useFundingTypeHook({
    ApplicationFunding,
    BankApplicationFunding,
  });

  const portalUrl = !isUpgradeFlowFromDashboard
    ? `${portalUrlApi}?flow=regular`
    : `${portalUrlApi}/buy`;

  const accountGtmType = retrieveFromSessionStorage(ACCOUNT_TYPE_GTM);

  useEffect(() => {
    if (
      ApplicantData?.PersonName &&
      ApplicantData?.PersonBirthday &&
      ApplicantData?.PersonAddress
    ) {
      gtmStepHelper({
        gtmStepEvent,
        eventName: ACCOUNT_STEP_VIEW,
        stepLabel: 'Kyc success',
        accountType: accountGtmType,
        accountName: ApplicantData?.PersonName,
        accountDateOfBirth: ApplicantData?.PersonBirthday,
        accountPhone: ApplicantData?.Phone,
        accountAddress: ApplicantData?.PersonAddress,
        customParams: {
          timestamp: new Date().toISOString(),
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGtmEvent = () => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_COMPLETE,
      stepLabel: 'Kyc success - Go to my account',
      accountType: accountGtmType,
      accountName: ApplicantData?.PersonName,
      accountDateOfBirth: ApplicantData?.PersonBirthday,
      accountPhone: ApplicantData?.Phone,
      customParams: {
        timestamp: new Date().toISOString(),
      },
    });
  };

  useEffect(() => {
    if (ApplicationFunding?.BankFundingType) {
      setFundingMethod(
        ` via ${parseEnumType(
          APPLICATION_FUNDING_TYPE,
          ApplicationFunding?.BankFundingType,
        )}`,
      );
    }
  }, [ApplicationFunding]);

  useEffect(() => {
    if (IraApplicationFundingResponse) {
      const iraFundingType = parseEnumType(
        IRA_APPLICATION_FUNDING_TYPES,
        IraApplicationFundingResponse?.IRAFundingType,
      );

      if (iraFundingType) {
        if (iraFundingType === IRA_APPLICATION_FUNDING_TYPES[0]) {
          setFundingMethod(' via IRA Transfer');
          return;
        }

        setFundingMethod(` via ${separateByUppercase(iraFundingType)}`);
      }
    }
  }, [IraApplicationFundingResponse]);

  useEffect(() => {
    if (MetalStreamData) {
      if (!MetalStreamData?.UseAccountBalance) {
        setIsMetalStreamPlaid(true);
      }

      setFundingMethod('');
    }
  }, [MetalStreamData]);

  const descriptions = [
    {
      title: 'kycSuccess.verifyTitle',
      description: 'kycSuccess.verifyDescription',
      Icon: FingerprintIcon,
    },
    {
      title:
        isPlaid || isMetalStreamPlaid
          ? t('kycSuccess.transferTitle')
          : t('kycSuccess.fundAccountTitle'),
      description:
        isPlaid || isMetalStreamPlaid
          ? t('kycSuccess.transferDescription')
          : t('kycSuccess.fundAccountDescription', {
              method: fundingMethod,
            }),
      Icon: () => <DataTransferIcon w="62px" h="62px" />,
    },
    {
      title: 'kycSuccess.metalsTitle',
      description: 'kycSuccess.metalsDescription',
      Icon: AccountingCoins,
    },
  ];

  const onComplete = () => {
    window.location.href = portalUrl;
    handleGtmEvent();
  };

  return (
    <BaseLayout canGoBack={false}>
      <Helmet>
        <title>
          {t('kycSuccess.titleTag', {
            brokerName: BrokerName,
          })}
        </title>
      </Helmet>
      <Header title={t('kycSuccess.title')} />
      <SelectAccountDescription
        options={descriptions}
        useDescriptionContainer
        onlyTitle={false}
      />
      <CTAContainer>
        <ButtonPrimary onClick={onComplete} title={t('kycSuccess.goto')} />
      </CTAContainer>
    </BaseLayout>
  );
};

export default KycSuccess;
